/**
 * Explore
 */
import { useMemo } from 'react';
import { TaskItem } from '@/components/TaskItem';
import { VerifyResult } from '@/components/dialogs/VerifyResult';
import { DrawResult } from '@/components/Draw/DrawResult';
import { useActivityStore } from '@/store/activity';
import { DrawAnimation, DelayDrawModal } from '@/components/Draw';
import { ConnectX } from '@/components/dialogs/ConnectX';
import { InstallBybitWallet } from '@/components/dialogs/InstallBybitWallet';
import { ActivityType, TaskStatus } from '@/types';
import { useWallet, useParams } from '@web3/base-hooks';
import { appPath } from '@web3/base-utils';
import { BybitWalletChromeExtensionLink, isMobile } from '@web3/base-utils';
import { TaskReward } from '@/components/TaskReward';
import { prepareTaskType } from '@/config/common';
import Blink from '@/components/dialogs/Blink';
import { useBybitWallet } from '@/hooks/wallet';
import {
  useMediaTask,
  useClickTask,
  useApplyTask,
  useCheckTask,
  useLuckyDraw,
  useInstallBW,
} from './hooks';

type ExploreProp = {
  handleShare: () => void;
  onShowSwitchWallet: () => void;
  onShowBanModal: () => void;
};

export function Explore(props: ExploreProp) {
  const { onShowSwitchWallet, onShowBanModal } = props;
  const { activityId } = useParams();
  const { walletId, walletType } = useWallet();
  const { walletAddresses } = useBybitWallet();
  const { locale } = useParams();

  const { showInstallBW, onChangeInstallBW } = useInstallBW();

  const activityStore = useActivityStore();

  const chainId = useMemo(
    () => activityStore.activityDetail?.activityInfo.chainId,
    [activityStore.activityDetail]
  );

  const commonQuery = useMemo(
    () => ({
      activityId: Number(activityId),
      chainId,
      excludeTaskType: prepareTaskType,
      walletType,
      walletId,
      walletAddress: walletAddresses.find((item) => +item.chainId === +chainId)
        ?.walletAddress,
    }),
    [walletType, activityId, chainId, walletAddresses.length]
  );

  const {
    showConnectX,
    showBlink,
    blinkData,
    onChangeShowConnectX,
    onChangeShowBlink,
    onChangeApplyObject,
    doBlinkTask,
    doMediaTask,
    doCallbackXTask,
    doCallbackBlinkTask,
  } = useMediaTask(commonQuery);

  const { doClickTask } = useClickTask();

  const {
    showVerifyResult,
    taskVerifyObject,
    handleTaskCheck,
    onChangeCurrentTask,
    onChangeShowVerifyResult,
  } = useCheckTask(commonQuery, onShowSwitchWallet);

  const { beginActivityApply } = useApplyTask(
    commonQuery,
    doMediaTask,
    doClickTask,
    doBlinkTask,
    onChangeInstallBW,
    onShowSwitchWallet,
    onChangeCurrentTask,
    onShowBanModal,
    onChangeApplyObject
  );

  const {
    showDrawResult,
    showDrawAnimation,
    showDelayDrawModal,
    onChangeShowDrawResult,
    onChangeDelayDrawModal,
    handleDraw,
  } = useLuckyDraw(commonQuery);

  const data = useMemo(() => {
    return activityStore.taskList || [];
  }, [activityStore]);

  const viewReward = () => {
    window.open(`/${locale}${appPath}/rewards`, '_blank');
  };

  return (
    <div className="explore-wrap relative main mt-6 !md:mt-10">
      {/* <h2 className="text-2xl font-semibold">{fm('word_explore')}</h2> */}
      {data?.map((item) => {
        const status = item.status;
        const verified =
          Number(status) >= TaskStatus.comleted &&
          Number(status) <= TaskStatus.ended;
        return (
          <div
            className="bg-area mt-6 flex flex-col !md:flex-row justify-between overflow-hidden"
            style={{
              boxShadow: isMobile ? '0px 0px 0px 0.5px #404347' : '',
            }}
            key={item.configId}
          >
            <TaskReward
              status={status}
              verified={verified}
              activityType={
                activityStore.activityDetail?.activityInfo?.activityType
              }
              data={item}
              handleDraw={handleDraw}
              viewReward={viewReward}
            />
            <TaskItem
              activityId={activityId}
              data={item}
              activityType={
                activityStore.activityDetail?.activityInfo?.activityType ||
                ActivityType.noStatus
              }
              onActivityApply={(subTask) => {
                beginActivityApply(subTask, item);
              }}
              handleTaskCheck={handleTaskCheck}
            />
          </div>
        );
      })}
      {/* 验证任务结果 */}
      <VerifyResult
        taskVerifyObject={taskVerifyObject}
        result={Boolean(activityStore.taskCheckResult?.checkResult)}
        visible={showVerifyResult}
        onOk={() => onChangeShowVerifyResult(false)}
        onCancel={() => onChangeShowVerifyResult(false)}
      />
      {/* 抽奖动画 */}
      {showDrawAnimation && <DrawAnimation />}
      <DelayDrawModal
        show={showDelayDrawModal}
        onConfirm={() => {
          onChangeDelayDrawModal(false);
        }}
        onClose={() => {
          onChangeDelayDrawModal(false);
        }}
      />
      {/* 抽奖结果 */}
      <DrawResult
        result={activityStore.rewardClaimResult}
        visible={showDrawResult}
        onOk={() => {
          onChangeShowDrawResult(false);
          activityStore.rewardClaimResult?.rewardStatus && viewReward();
        }}
        onCancel={() => onChangeShowDrawResult(false)}
        onShare={() => {
          onChangeShowDrawResult(false);
          props.handleShare();
        }}
      />
      {/* 连接推特 */}
      <ConnectX
        show={showConnectX}
        onConnect={doCallbackXTask}
        onCancel={() => {
          onChangeShowConnectX(false);
        }}
      />
      {/* 安装Bybitwallet提示弹窗 */}
      <InstallBybitWallet
        visible={showInstallBW}
        onOK={() => {
          window.top?.open(BybitWalletChromeExtensionLink);
        }}
        onCancel={() => {
          onChangeInstallBW(false);
        }}
      />

      {/* blink弹窗 */}
      <Blink
        data={blinkData}
        visible={showBlink}
        onPost={doCallbackBlinkTask}
        onCancel={() => {
          onChangeShowBlink(false);
        }}
      />
    </div>
  );
}
